import axios, { AxiosError, AxiosHeaders, AxiosProgressEvent, AxiosResponse } from 'axios';

import { BYTES_IN_KB, BYTES_IN_MB } from '~constants/common';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export const get = (url: string, params?: any, headers?: AxiosHeaders, signal?: AbortSignal) =>
  sendRequest('GET', url, { params, headers, signal });

export const post = (url: string, data?: any) => sendRequest('POST', url, { data });

export const put = (url: string, data: any) => sendRequest('PUT', url, { data });

export const patch = (url: string, data: any) => sendRequest('PATCH', url, { data });

export const remove = (url: string, params?: any) => sendRequest('DELETE', url, { params });

export const downloadFile = async (url: string, fileName: string): Promise<string> => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });

    if (response.status !== 200) {
      throw new Error('Failed to fetch file from URL');
    }

    const file = new File([response.data], fileName, { type: response.data.type });
    const objectUrl = URL.createObjectURL(file);

    return objectUrl;
  } catch (error) {
    const errorMessage = await buildBlobErrorResponse(error);
    throw new Error(errorMessage);
  }
};

export const download = async (
  url: string,
  params?: any,
  onProgress?: (value: string) => void,
  headers?: AxiosHeaders,
) => {
  try {
    const downloadHeaders = {
      ...headers,
      'Access-Control-Expose-Headers': 'Content-Disposition',
    };

    const file = await axios({
      method: 'GET',
      url,
      params,
      responseType: 'blob',
      headers: downloadHeaders,
      onDownloadProgress: (progressEvent) => onProgress && onProgress(calculateProgress(progressEvent)),
    });

    return file;
  } catch (error) {
    const errorMessage = await buildBlobErrorResponse(error);
    throw new Error(errorMessage);
  }
};

/** Saves a downloaded file under the name in its Content-Disposition (as the export dialog does). */
export const saveDownload = (payload: AxiosResponse, fallbackFilename: string): void => {
  const contentDisposition = payload.headers['content-disposition'];
  const filename = contentDisposition ? contentDisposition.split('=')[1] : fallbackFilename;

  // Create a link adding the received data into navigateable link and provide to user
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(new Blob([payload.data]));
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const upload = async (
  url: string,
  file: File,
  onProgress?: (progress: string | number) => void,
  additionalData?: Record<string, any>,
  headers?: AxiosHeaders,
  rawProgress?: boolean,
) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await axios({
      method: 'POST',
      url,
      data: formData,
      headers: {
        ...headers,
      },
      onUploadProgress: (event) => {
        if (onProgress) {
          onProgress(getProgressValue(event, rawProgress ?? false));
        }
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(buildErrorResponse(error));
  }
};

const sendRequest = async (
  method: HttpMethod,
  url: string,
  options: {
    data?: any;
    params?: any;
    headers?: AxiosHeaders;
    signal?: AbortSignal;
  } = {},
): Promise<any> => {
  try {
    const response = await axios({ method, url, ...options });
    return response.data ?? true;
  } catch (error) {
    throw new Error(buildErrorResponse(error));
  }
};

const buildErrorResponse = (error: unknown) => {
  if (error instanceof AxiosError) {
    if (error.response) {
      // Request was made and the API responded with a status (and message)
      return (
        error.response.data?.message ??
        'Request was rejected on the server with an unknown reasoning. Please contact your system administrator'
      );
    }
    if (error.code) {
      // Request was made but no response was received
      switch (error.code) {
        case 'ERR_NETWORK': {
          return 'Network error: No response received from API. Please contact your administrator';
        }
        default: {
          return `Unknown error ${error.code}: ${error.message}`;
        }
      }
    }
  }

  // Error was raised which has not been accounted for above
  if (error instanceof Error) return `Unknown error occurred: ${error.message}`;

  // Unknown failure with an unknown Error type
  return 'Unknown error occurred. Please contact your administrator';
};

const buildBlobErrorResponse = async (error: unknown): Promise<string> => {
  if (error instanceof AxiosError && error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      const json = JSON.parse(text);
      return json.message || 'Request failed';
    } catch {
      // If blob parsing fails, fall back to standard error handling
      return buildErrorResponse(error);
    }
  }

  // Not a blob error, use standard error handling
  return buildErrorResponse(error);
};

const calculateProgress = (progressEvent: AxiosProgressEvent) => {
  let value;
  let suffix;

  if (progressEvent.total) {
    value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    suffix = '%';
  } else {
    value = progressEvent.loaded;
    if (value > BYTES_IN_MB) {
      value /= BYTES_IN_MB;
      suffix = 'mb';
    } else if (value > BYTES_IN_KB) {
      value /= BYTES_IN_KB;
      suffix = 'kb';
    } else {
      suffix = 'b';
    }
  }

  value = Math.round(value * 100) / 100;
  return `${value}${suffix}`;
};

const getProgressValue = (event: AxiosProgressEvent, raw: boolean): number | string => {
  if (raw && event.total) {
    return Math.round((event.loaded * 100) / event.total);
  }
  return calculateProgress(event);
};
